import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, Wrench, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Dashboard reminders — next-service due + outstanding job recommendations.
 * Reads /api/reminders (saved data only; never invents dates or work).
 */
export function ServiceRemindersCard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api("/reminders").then(setData).catch(() => setData(null));
  }, []);

  if (!data) return null;
  const { service_due = [], recommendations = [], counts = {} } = data;
  if (service_due.length === 0 && recommendations.length === 0) return null;

  const dueTag = (r) => {
    if (r.due_date && r.days_until != null)
      return r.days_until < 0 ? `${Math.abs(r.days_until)}d overdue` : r.days_until === 0 ? "due today" : `in ${r.days_until}d`;
    if (r.miles_remaining != null)
      return r.miles_remaining <= 0 ? `${Math.abs(r.miles_remaining).toLocaleString()} mi over` : `${r.miles_remaining.toLocaleString()} mi to go`;
    return r.overdue ? "due" : "";
  };
  const dueSub = (r) => {
    const parts = [];
    if (r.due_date) parts.push(`Service due ${r.due_date}`);
    if (r.due_mileage) parts.push(`at ${r.due_mileage.toLocaleString()} mi${r.current_mileage != null ? ` (now ${r.current_mileage.toLocaleString()})` : ""}`);
    if (r.customer_name) parts.push(r.customer_name);
    return parts.join(" · ");
  };

  return (
    <div className="mb-6 rounded-xl border border-border bg-card p-4" data-testid="dashboard-reminders">
      <div className="mb-3 flex items-center gap-2">
        <CalendarClock className="h-4 w-4 text-brand" />
        <h3 className="text-xs font-extrabold uppercase tracking-widest text-muted-foreground">Reminders</h3>
        <div className="ml-auto flex gap-2">
          {counts.service_due > 0 && (
            <span className={`inline-flex h-5 items-center rounded-full px-2 text-[11px] font-extrabold ${counts.overdue > 0 ? "bg-danger-bg text-danger" : "bg-brand-tertiary text-brand"}`}>
              {counts.service_due} service
            </span>
          )}
          {counts.recommendations > 0 && (
            <span className="inline-flex h-5 items-center rounded-full bg-amber-100 px-2 text-[11px] font-extrabold text-amber-700">
              {counts.recommendations} advisory
            </span>
          )}
        </div>
      </div>

      {service_due.length > 0 && (
        <div className="mb-2 space-y-1.5" data-testid="reminders-service-due">
          {service_due.slice(0, 5).map((r) => (
            <button
              key={r.vehicle_id}
              type="button"
              onClick={() => navigate(`/vehicles/${r.vehicle_id}`)}
              data-testid={`reminder-service-${r.vehicle_id}`}
              className="flex w-full items-center gap-3 rounded-lg border border-border bg-background px-3 py-2 text-left transition-colors hover:border-brand"
            >
              <CalendarClock className={`h-4 w-4 ${r.overdue ? "text-danger" : "text-brand"}`} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-midnight">{r.vrm} · {r.make} {r.model}</p>
                <p className="truncate text-xs text-muted-foreground">{dueSub(r)}</p>
              </div>
              <span className={`whitespace-nowrap text-xs font-extrabold ${r.overdue ? "text-danger" : "text-brand"}`}>{dueTag(r)}</span>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </button>
          ))}
        </div>
      )}

      {recommendations.length > 0 && (
        <div className="space-y-1.5" data-testid="reminders-advisories">
          {recommendations.slice(0, 5).map((r) => (
            <button
              key={r.vehicle_id}
              type="button"
              onClick={() => navigate(`/vehicles/${r.vehicle_id}`)}
              data-testid={`reminder-advisory-${r.vehicle_id}`}
              className="flex w-full items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-left transition-colors hover:border-amber-400"
            >
              <Wrench className="h-4 w-4 text-amber-600" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-midnight">{r.vrm} · {r.make} {r.model}</p>
                <p className="truncate text-xs text-amber-700">{r.items[0]?.description || "Outstanding recommendation"}{r.customer_name ? ` · ${r.customer_name}` : ""}</p>
              </div>
              <span className="whitespace-nowrap rounded-full bg-amber-500 px-2 py-0.5 text-[11px] font-extrabold text-white">{r.count}</span>
              <ArrowRight className="h-4 w-4 text-amber-600" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
